import { type ButlerTabType } from '@cart-services/constants/butler-tab'
import { type PlatformType } from '@cart-services/hooks/fp-b2b/store-map'
import { type CountryCode, type ProjectCode } from '@constants'
import { createContext, useMemo, type PropsWithChildren } from 'react'
import { getButlerCartPath, getButlerCsvRecommendationsPath, getButlerEntryPath, getButlerRecommendationsPath } from '../../constants'

export interface ButlerProviderProps {
  platform: PlatformType
  countryCode: CountryCode
  projectCode: ProjectCode
  campaignId: number
  campaignCode: string
  /** 平台路由前綴，例如 /merchant/dcs001 或 /shepherdtech-market */
  basePath: string
  selectedTab: ButlerTabType
  onSelectedTabChange: (tab: ButlerTabType) => void
  /** 加入購物車成功、導向購物車頁之前呼叫；STM 用來把購物車頁的品牌 tab 切到建單品牌 */
  onAddToCartSuccess?: () => void
}

export interface ButlerRoutePaths {
  entry: string
  recommendations: string
  csvRecommendations: string
  cart: string
}

export interface ButlerContextValue extends ButlerProviderProps {
  routes: ButlerRoutePaths
}

export const ButlerContext = createContext<ButlerContextValue | null>(null)

export const ButlerProvider = (props: PropsWithChildren<ButlerProviderProps>) => {
  const { children, platform, projectCode, countryCode, campaignId, campaignCode, basePath, selectedTab, onSelectedTabChange, onAddToCartSuccess } = props

  const contextValue = useMemo<ButlerContextValue>(
    () => ({
      platform,
      projectCode,
      countryCode,
      campaignId,
      campaignCode,
      basePath,
      selectedTab,
      onSelectedTabChange,
      onAddToCartSuccess,
      routes: {
        entry: getButlerEntryPath(basePath, platform),
        recommendations: getButlerRecommendationsPath(basePath, platform),
        csvRecommendations: getButlerCsvRecommendationsPath(basePath, platform),
        cart: getButlerCartPath(basePath),
      },
    }),
    [platform, projectCode, countryCode, campaignId, campaignCode, basePath, selectedTab, onSelectedTabChange, onAddToCartSuccess],
  )

  return (
    <ButlerContext.Provider value={contextValue}>
      {children}
    </ButlerContext.Provider>
  )
}
